import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { GmOnline } from '../../api/types';
import { t } from '../../i18n/idioma';
import type { RegistroMapa } from '../../world/mapa';

/** Uma linha do Registro do GM (techspec-gm-menu §05): o que ele fez nesta sessão e, quando dá, como desfazer. */
export interface Feito {
  readonly id: number;
  readonly hora: string;
  readonly cat: string;
  readonly txt: string;
  desfazer?: (() => Promise<unknown>) | null;
}

/** O que uma ação manda para o Registro. `cat` e `txt` já traduzidos. */
export interface ParaRegistro {
  readonly cat: string;
  readonly txt: string;
  readonly desfazer?: () => Promise<unknown>;
  /** O jogador em quem a ação foi feita: entra nos Recentes. */
  readonly alvo?: GmOnline | null;
}

/** Roda uma ação com o "ocupado", o erro e o Registro de uma vez. Devolve se deu certo. */
export type Rodar = (fn: () => Promise<unknown>, reg?: ParaRegistro) => Promise<boolean>;

export const agoraHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function chave(s: string) {
  return s.trim().toLowerCase().replace(/\s+/g, '');
}

/** O id do mapa pelo rótulo da cidade que o servidor manda (`Minoc`, `Serpent's Hold`). */
export function idDaCidade(label: string, cidades: readonly RegistroMapa[]) {
  const k = chave(label);
  return cidades.find(c => chave(c.nome) === k || chave(c.id) === k || chave(c.homeCity) === k)?.id ?? '';
}

/**
 * O botão das ações perigosas (matar, tirar da guilda, cancelar evento, aplicar a hora…): o primeiro clique arma e
 * mostra "Confirmar …?"; o segundo, em até 4 s, executa. Sozinho, desarma.
 */
export function BotaoPerigoso({ rotulo, confirmar, disabled, onConfirm, className = '' }: {
  rotulo: ReactNode; confirmar: string; disabled?: boolean; onConfirm(): void; className?: string;
}) {
  const [armado, setArmado] = useState(false);
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => { if (disabled) setArmado(false); }, [disabled]);
  return (
    <button
      type="button"
      className={`btn ${armado ? 'btn--conf' : 'btn--perigo'} ${className}`}
      disabled={disabled}
      onClick={() => {
        if (armado) {
          window.clearTimeout(timer.current);
          setArmado(false);
          onConfirm();
          return;
        }
        setArmado(true);
        timer.current = window.setTimeout(() => setArmado(false), 4000);
      }}
      onKeyDown={e => { if (e.key === 'Escape' && armado) { e.stopPropagation(); setArmado(false); } }}
    >
      {armado ? confirmar : rotulo}
    </button>
  );
}

/* ---------- Recentes e Fixados (no navegador do GM; listas curtas, nunca o shard) ---------- */

export interface JogadorSalvo { readonly id: string; readonly name: string }

const CHAVE_RECENTES = 'uoidle.gm.recentes';
const CHAVE_FIXADOS = 'uoidle.gm.fixados';
export const MAX_RECENTES = 15;

function ler(chaveLs: string): JogadorSalvo[] {
  try {
    const v = JSON.parse(localStorage.getItem(chaveLs) ?? '[]');
    return Array.isArray(v) ? v.filter(x => x && typeof x.id === 'string' && typeof x.name === 'string').slice(0, 50) : [];
  } catch {
    return [];
  }
}

function gravar(chaveLs: string, lista: readonly JogadorSalvo[]) {
  try { localStorage.setItem(chaveLs, JSON.stringify(lista)); } catch { /* sem armazenamento: vale só nesta sessão */ }
}

export function useListasDoGm() {
  const [recentes, setRecentes] = useState<JogadorSalvo[]>(() => ler(CHAVE_RECENTES));
  const [fixados, setFixados] = useState<JogadorSalvo[]>(() => ler(CHAVE_FIXADOS));
  useEffect(() => gravar(CHAVE_RECENTES, recentes), [recentes]);
  useEffect(() => gravar(CHAVE_FIXADOS, fixados), [fixados]);
  const tocar = (j: JogadorSalvo) =>
    setRecentes(l => [{ id: j.id, name: j.name }, ...l.filter(x => x.id !== j.id)].slice(0, MAX_RECENTES));
  const alternarFixado = (j: JogadorSalvo) =>
    setFixados(l => (l.some(x => x.id === j.id) ? l.filter(x => x.id !== j.id) : [{ id: j.id, name: j.name }, ...l].slice(0, 30)));
  return { recentes, fixados, tocar, alternarFixado };
}

/* ---------- ícones do trilho ---------- */

const TRACOS: Readonly<Record<string, string>> = {
  painel: 'M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z',
  jogadores: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21v-1a6 6 0 0 1 12 0v1M17 11a3 3 0 1 0 0-6M22 21v-1a5 5 0 0 0-4-4.9',
  mundo: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z',
  eventos: 'M12 2l2.8 6.2 6.7.6-5 4.5 1.5 6.6L12 16.6 6 19.9l1.5-6.6-5-4.5 6.7-.6z',
  itens: 'M3 7l9-4 9 4-9 4-9-4zM3 7v10l9 4 9-4V7M12 11v10',
  skins: 'M8 3l4 2 4-2 5 4-3 3-2-1v12H8V9L6 10 3 7z',
  registro: 'M5 3h11l3 3v15H5zM8 9h8M8 13h8M8 17h5'
};

export function IconeGm({ nome }: { nome: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round" strokeLinecap="round" aria-hidden="true">
      <path d={TRACOS[nome] ?? ''} />
    </svg>
  );
}

/** "3.212" vira "3,2k" no trilho. */
export function contagemCurta(n: number, idioma: string) {
  if (n < 1000) return String(n);
  return `${(n / 1000).toLocaleString(idioma, { maximumFractionDigits: 1 })}k`;
}

export const rotuloCanal = (canal?: number) => t('gm.x.canalCurto', { n: canal ?? 0 });
